import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import Icon from "../Icon";
import Modal from "../Modal";
import Button from "../Button";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import api from "../../services/api";
import {
    subscribeNotifications,
    getNotifications,
    refreshNotifications,
    reportPlatformSeen
} from "../../services/notifications";
import "./styles.scss";

export const NotificationBell = () => {
    const navigate = useNavigate();
    const { notifications, unreadCount } = useSyncExternalStore(subscribeNotifications, getNotifications);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const [announcement, setAnnouncement] = useState(null);
    const wrapperRef = useRef(null);
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current && !wrapperRef.current.contains(e.target) &&
                (!dropdownRef.current || !dropdownRef.current.contains(e.target))
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Impressão: o que está na lista com o dropdown aberto foi visto. O store
    // ignora id já reportado, então reabrir não conta de novo.
    useEffect(() => {
        if (!open) return;

        reportPlatformSeen(
            notifications
                .filter(notification => notification.type === 'platform')
                .map(notification => notification.id)
        );
    }, [open, notifications]);

    // Ancora por `bottom` quando não há espaço abaixo do sino (ex.: sino no
    // rodapé da sidebar), para o dropdown crescer para cima a partir do botão.
    const updatePosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const width = 320;
            const maxHeight = 420;
            const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
            const spaceBelow = window.innerHeight - rect.bottom;

            if (spaceBelow < maxHeight + 8 && rect.top > spaceBelow) {
                setPos({ bottom: window.innerHeight - rect.top + 8, left });
            } else {
                setPos({ top: rect.bottom + 8, left });
            }
        }
    };

    useEffect(() => {
        if (!open) return;
        window.addEventListener('resize', updatePosition);
        return () => window.removeEventListener('resize', updatePosition);
    }, [open]);

    const toggleOpen = () => {
        setOpen(prev => {
            if (!prev) updatePosition();
            return !prev;
        });
    };

    const markAllRead = () => {
        api.put('/notifications/read-all').then(() => {
            refreshNotifications();
        }).catch(() => {});
    };

    const openMention = (notification) => {
        const goToCard = () => {
            if (notification.card_id) {
                navigate(`/crm/card/${notification.card_id}`);
            }
        };

        if (notification.read_at) {
            goToCard();
            return;
        }

        api.put(`/notifications/mention/${notification.id}/read`)
            .then(() => refreshNotifications())
            .catch(() => {})
            .then(goToCard);
    };

    const openAnnouncement = (notification) => {
        setAnnouncement(notification);

        api.put(`/notifications/platform/${notification.id}/opened`)
            .then(() => refreshNotifications())
            .catch(() => {});
    };

    const openCredit = (notification) => {
        if (!notification.read_at) {
            api.put('/notifications/credit/read')
                .then(() => refreshNotifications())
                .catch(() => {});
        }

        navigate(notification.cta_url || '/credits');
    };

    const openNotification = (notification) => {
        setOpen(false);

        if (notification.type === 'platform') {
            openAnnouncement(notification);
            return;
        }

        if (notification.type === 'credit') {
            openCredit(notification);
            return;
        }

        openMention(notification);
    };

    const clickAnnouncementLink = () => {
        api.put(`/notifications/platform/${announcement.id}/clicked`).catch(() => {});
        window.open(announcement.cta_url, '_blank', 'noopener,noreferrer');
    };

    const renderItemText = (notification) => {
        if (notification.type === 'platform' || notification.type === 'credit') {
            return (
                <>
                    <span className="title">{notification.title}</span>
                    {notification.excerpt &&
                        <span className="excerpt">{notification.excerpt}</span>
                    }
                </>
            );
        }

        return (
            <>
                <span className="title">
                    <b>{notification.mentioned_by ?? 'Alguém'}</b> mencionou você
                    {notification.card_code ? ` no card #${notification.card_code}` : ''}
                </span>
                {notification.excerpt &&
                    <span className="excerpt">{notification.excerpt}</span>
                }
            </>
        );
    };

    return (
        <div className="notification-bell" ref={wrapperRef}>
            <button
                type="button"
                className="bell-button"
                ref={buttonRef}
                onClick={toggleOpen}
            >
                <Icon name="bell" />
                {unreadCount > 0 &&
                    <span className="badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                }
            </button>

            {open && createPortal(
                <div className="notification-dropdown" ref={dropdownRef} style={pos}>
                    <div className="dropdown-header">
                        <strong>Notificações</strong>
                        {unreadCount > 0 &&
                            <a href="#" onClick={e => { e.preventDefault(); markAllRead(); }}>
                                Marcar todas como lidas
                            </a>
                        }
                    </div>
                    <div className="dropdown-list">
                        {notifications.length === 0 &&
                            <p className="empty">Nenhuma notificação.</p>
                        }
                        {notifications.map(notification => (
                            <button
                                type="button"
                                key={`${notification.type}-${notification.id}`}
                                className={`notification-item${notification.read_at ? '' : ' unread'}${notification.type === 'platform' ? ' platform' : ''}`}
                                onClick={() => openNotification(notification)}
                            >
                                {renderItemText(notification)}
                                <span className="time">{moment(notification.created_at).fromNow()}</span>
                            </button>
                        ))}
                    </div>
                </div>,
                document.body
            )}

            <Modal
                title={announcement?.title ?? ''}
                show={announcement !== null}
                close={() => setAnnouncement(null)}
                size="big"
                className="announcement-modal"
                footer={
                    announcement?.cta_url
                        ? <Button
                            type="primary"
                            size="small"
                            text={announcement.cta_label || 'Saiba mais'}
                            action={clickAnnouncementLink}
                        />
                        : null
                }
            >
                {announcement &&
                    <div className="announcement">
                        <span className="announcement-date">
                            {moment(announcement.created_at).format('DD/MM/YYYY HH:mm')}
                        </span>

                        {/* HTML é sanitizado no backend (App\Support\RichTextSanitizer). */}
                        <div
                            className="announcement-body"
                            dangerouslySetInnerHTML={{ __html: announcement.body_html ?? '' }}
                        />
                    </div>
                }
            </Modal>
        </div>
    );
};

export default NotificationBell;
